import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
  buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/dbsdefinitions';
const CUSTOMER_API = '/api/customers';

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  bankName: { type: 'text', odataField: 'BankName' },
  currencyCode: { type: 'text', odataField: 'CurrencyCode' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }] },
};
const SEARCH_FIELDS = ['TextId', 'BankName', 'CurrencyCode', 'Customer/Title', 'Customer/TextId'];

function parseDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatMoney(value, currencyCode = 'TRY') {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('tr-TR', { style: 'currency', currency: currencyCode || 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
}

function calculateNetLimit(row) {
  return parseDecimal(row.totalLimit) - parseDecimal(row.dealerCashLoanAmount);
}

function calculateAvailableLimit(row) {
  return calculateNetLimit(row) - parseDecimal(row.ddsInvoiceAmount);
}

function customerLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const name = item.title || item.Title || item.shortName || item.ShortName || '';
  return name ? `${code} - ${name}` : code;
}

export function DbsDefinitionsPage() {
  usePageMeta('DBS', 'DBS definition kayıtlarını filtreleyin, sıralayın ve yönetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: görünür kolonların OData alanları + always. Müşteri kolonu CustomerId (lazy
  // etiket), para kolonları TotalLimit/DealerCashLoanAmount/DdsInvoiceAmount'tan hesaplanır
  // (COLUMN_FILTER_META'da yok) ve CurrencyCode kolonu gizliyken bile para biçimlemesinde
  // kullanılır → hepsi `always`'e eklenir.
  const selectFields = useMemo(
    () => buildSelectFields({
      columnFilterMeta: COLUMN_FILTER_META,
      columnVisibility,
      always: ['Id', 'CustomerId', 'TotalLimit', 'DealerCashLoanAmount', 'DdsInvoiceAmount', 'CurrencyCode'],
    }),
    [columnVisibility],
  );

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'BankName',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : ''), [customerLabels]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `${CUSTOMER_API}?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = customerLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId || r.customer?.id).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  const openNew = () => navigate('/dbs/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/dbs/${row.id}`, { state: { row } }); };
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/dbs/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'DBS kaydı silinemedi');
      showToast('success', 'DBS kaydı silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'DBS Ref', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.display({
      id: 'customer', header: 'Müşteri',
      cell: ({ row }) => customerName(row.original.customerId || row.original.customer?.id) || '-',
    }),
    columnHelper.accessor('bankName', { header: 'Banka', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('totalLimit', {
      header: 'Toplam Limit', enableColumnFilter: false,
      cell: ({ row }) => formatMoney(row.original.totalLimit, row.original.currencyCode),
    }),
    columnHelper.display({
      id: 'netLimit', header: 'Net Limit', enableSorting: false,
      cell: ({ row }) => formatMoney(calculateNetLimit(row.original), row.original.currencyCode),
    }),
    columnHelper.display({
      id: 'availableDdsLimit', header: 'Kullanılabilir Limit', enableSorting: false,
      cell: ({ row }) => formatMoney(calculateAvailableLimit(row.original), row.original.currencyCode),
    }),
    columnHelper.accessor('currencyCode', { header: 'Para Birimi', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() !== false ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="bad">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerName, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('BankName', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'DBS Ref' },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId || row.customer?.id) },
        { field: 'bankName', headerName: 'Banka' },
        { headerName: 'Toplam Limit', valueGetter: ({ row }) => parseDecimal(row.totalLimit) },
        { headerName: 'Net Limit', valueGetter: ({ row }) => calculateNetLimit(row) },
        { headerName: 'Kullanılabilir Limit', valueGetter: ({ row }) => calculateAvailableLimit(row) },
        { field: 'currencyCode', headerName: 'Para Birimi' },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive !== false ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `dbs-definition_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri, banka veya referans ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="DBS"
        subtitle="DBS definition kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni DBS Tanımı</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected.textId}"</strong> DBS kaydını silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
